import { useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Grid,
  MenuItem,
  alpha,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import type { LocationType } from '../../types';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';

interface CreateLocationData {
  name: string;
  type: LocationType;
  code?: string;
  description?: string;
  address?: string;
  city?: string;
  wilaya?: string;
  zipCode?: string;
  country: string;
  phone?: string;
  email?: string;
  currency?: string;
  canSellToCustomers: boolean;
  canFulfillOnlineOrders: boolean;
  canReceiveTransfers: boolean;
  canSendTransfers: boolean;
  isActive: boolean;
}

export const LocationCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();

  const defaultType = (searchParams.get('type') as LocationType) || 'store';

  const [formData, setFormData] = useState<CreateLocationData>({
    name: '',
    type: defaultType,
    code: '',
    description: '',
    address: '',
    city: '',
    wilaya: '',
    zipCode: '',
    country: 'Algeria',
    phone: '',
    email: '',
    currency: 'DZD',
    canSellToCustomers: defaultType === 'store',
    canFulfillOnlineOrders: true,
    canReceiveTransfers: true,
    canSendTransfers: defaultType === 'warehouse',
    isActive: true,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateLocationData) => {
      const endpoint = data.type === 'store' ? '/locations/store' : '/locations/warehouse';
      return axiosInstance.post(endpoint, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      showMessage('Location created successfully', 'success');
      navigate('/app/locations');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to create location', 'error');
    },
  });

  const handleChange = (field: keyof CreateLocationData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const type = e.target.value as LocationType;
    setFormData((prev) => ({
      ...prev,
      type,
      canSellToCustomers: type === 'store',
      canSendTransfers: type === 'warehouse',
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  return (
    <Box sx={{ p: 3, ...animations.fadeIn }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/app/locations')}
          sx={{ color: palette.textSecondary }}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: typography.fontWeight.semibold }}>
          Create Location
        </Typography>
      </Box>

      <Card sx={{ bgcolor: mode === 'dark' ? palette.surface : palette.light.surface }}>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Basic Info */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Name"
                  value={formData.name}
                  onChange={handleChange('name')}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  required
                  select
                  label="Type"
                  value={formData.type}
                  onChange={handleTypeChange}
                >
                  <MenuItem value="store">Store</MenuItem>
                  <MenuItem value="warehouse">Warehouse</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Code"
                  value={formData.code}
                  onChange={handleChange('code')}
                  placeholder="e.g., WH-001"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Description"
                  value={formData.description}
                  onChange={handleChange('description')}
                />
              </Grid>

              {/* Address */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Address
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={handleChange('address')}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={handleChange('city')}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Wilaya"
                  value={formData.wilaya}
                  onChange={handleChange('wilaya')}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Zip Code"
                  value={formData.zipCode}
                  onChange={handleChange('zipCode')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={formData.country}
                  onChange={handleChange('country')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Currency"
                  value={formData.currency}
                  onChange={handleChange('currency')}
                >
                  <MenuItem value="DZD">DZD - Algerian Dinar</MenuItem>
                  <MenuItem value="EUR">EUR - Euro</MenuItem>
                </TextField>
              </Grid>

              {/* Contact */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Contact
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={handleChange('phone')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  value={formData.email}
                  onChange={handleChange('email')}
                />
              </Grid>

              {/* Capabilities */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Capabilities
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.canSellToCustomers}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, canSellToCustomers: e.target.checked }))
                      }
                    />
                  }
                  label="Can sell to customers"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.canFulfillOnlineOrders}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, canFulfillOnlineOrders: e.target.checked }))
                      }
                    />
                  }
                  label="Can fulfill online orders"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.canReceiveTransfers}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, canReceiveTransfers: e.target.checked }))
                      }
                    />
                  }
                  label="Can receive transfers"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.canSendTransfers}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, canSendTransfers: e.target.checked }))
                      }
                    />
                  }
                  label="Can send transfers"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, isActive: e.target.checked }))
                      }
                    />
                  }
                  label="Active"
                />
              </Grid>

              {/* Actions */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                  <Button onClick={() => navigate('/app/locations')}>Cancel</Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={createMutation.isPending}
                    sx={{
                      bgcolor: palette.primary,
                      '&:hover': { bgcolor: alpha(palette.primary, 0.9) },
                    }}
                  >
                    {createMutation.isPending ? 'Creating...' : 'Create Location'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LocationCreate;
